import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import {
  Building2, Target, Sparkles, CalendarClock, CheckSquare, Award, MessageCircle, LayoutDashboard,
  Radio, Zap, Package, Ticket, User, Loader2,
} from "lucide-react";
import api from "@/lib/api";

const NAV_ITEMS = [
  { label: "Command Center", to: "/", icon: LayoutDashboard, group: "Navigate" },
  { label: "Universe", to: "/universe", icon: Building2, group: "Navigate" },
  { label: "Signals", to: "/signals", icon: Radio, group: "Navigate" },
  { label: "Leads", to: "/leads", icon: Zap, group: "Navigate" },
  { label: "Opportunities", to: "/opportunities", icon: Target, group: "Navigate" },
  { label: "Orders", to: "/orders", icon: Package, group: "Navigate" },
  { label: "Tickets", to: "/tickets", icon: Ticket, group: "Navigate" },
  { label: "Meetings", to: "/meetings", icon: CalendarClock, group: "Navigate" },
  { label: "My Work", to: "/tasks", icon: CheckSquare, group: "Navigate" },
  { label: "Partners", to: "/partners", icon: Award, group: "Navigate" },
  { label: "Ergoniq Team", to: "/team", icon: User, group: "Navigate" },
  { label: "Ask Niq", to: "/chat", icon: MessageCircle, group: "Navigate" },
];

const TYPE_META = {
  company: { label: "Companies", icon: Building2 },
  contact: { label: "Contacts", icon: User },
  signal: { label: "Signals", icon: Radio },
  lead: { label: "Leads", icon: Zap },
  opportunity: { label: "Opportunities", icon: Target },
  order: { label: "Orders", icon: Package },
  ticket: { label: "Tickets", icon: Ticket },
};

const GROUP_ORDER = ["Companies", "Contacts", "Signals", "Leads", "Opportunities", "Orders", "Tickets"];

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tookMs, setTookMs] = useState(null);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setLoading(false);
      setTookMs(null);
    }
  }, [open]);

  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange((v) => !v);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [onOpenChange]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      setTookMs(null);
      return undefined;
    }

    const id = ++reqRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q, limit: 8 } });
        if (reqRef.current !== id) return;
        setResults(data.results || []);
        setTookMs(data.took_ms ?? null);
      } catch {
        if (reqRef.current !== id) return;
        setResults([]);
        setTookMs(null);
      } finally {
        if (reqRef.current === id) setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  const go = useCallback((path) => {
    onOpenChange(false);
    navigate(path);
  }, [navigate, onOpenChange]);

  const grouped = GROUP_ORDER.map((heading) => {
    const typeKey = Object.entries(TYPE_META).find(([, m]) => m.label === heading)?.[0];
    const items = results.filter((r) => r.type === typeKey);
    if (!items.length) return null;
    const Icon = TYPE_META[typeKey]?.icon || Sparkles;
    return (
      <CommandGroup key={heading} heading={heading}>
        {items.map((r) => (
          <CommandItem
            key={`${r.type}-${r.id}`}
            value={`${r.type} ${r.title} ${r.subtitle}`}
            onSelect={() => go(r.path)}
            data-testid={`search-result-${r.type}-${r.entity_id || r.id}`}
          >
            <Icon size={14} className="mr-2 shrink-0 text-[var(--copper)]" />
            <div className="flex flex-col min-w-0">
              <span className="truncate text-[13px]">{r.title}</span>
              {r.subtitle && <span className="truncate text-[11px] text-[var(--text-dim)]">{r.subtitle}</span>}
            </div>
          </CommandItem>
        ))}
      </CommandGroup>
    );
  }).filter(Boolean);

  const showNav = query.trim().length < 2;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search companies, contacts, signals, leads, opportunities, orders, tickets…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList>
        {loading && (
          <div className="flex items-center gap-2 px-4 py-3 text-[12px] text-[var(--text-muted)]" data-testid="search-loading">
            <Loader2 size={14} className="animate-spin text-[var(--copper)]" />
            Searching…
          </div>
        )}
        {!loading && query.trim().length >= 2 && grouped.length === 0 && (
          <CommandEmpty>No results for &ldquo;{query.trim()}&rdquo;.</CommandEmpty>
        )}
        {showNav && (
          <CommandGroup heading="Navigate">
            {NAV_ITEMS.map((it) => (
              <CommandItem
                key={it.to}
                value={it.label}
                onSelect={() => go(it.to)}
                data-testid={`palette-${it.to}`}
              >
                <it.icon size={14} className="mr-2" />
                {it.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {!showNav && grouped}
        {!loading && tookMs != null && query.trim().length >= 2 && (
          <>
            <CommandSeparator />
            <div className="px-3 py-2 text-[10px] text-[var(--text-dim)] font-mono" data-testid="search-timing">
              {results.length} result{results.length !== 1 ? "s" : ""} · {tookMs}ms
            </div>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
